import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { ensureMsalInitialized, msalInstance } from "./auth/msal.config";

import BudgetEditor from "./components/BudgetEditor";
import LoginView from "./components/LoginView";
import ChangePasswordView from "./components/ChangePasswordView";
import ResetPasswordView from "./components/ResetPasswordView";
import EntraCallback from "./components/EntraCallback";
import EntraLoginButton from "./components/EntraLoginButton";
import HeadcountEditor from "./components/HeadcountEditor";
import Dashboard from "./components/Dashboard";
import AdminPanel from "./components/AdminPanel";
import SalaryView from "./components/SalaryView";
import TempLaborView from "./components/TempLaborView";
import YTGSimulationStrip from "./components/YTGSimulationStrip";
// import TeamView from "./components/TeamView";  // replaced by ActualsDashboard
import ActualsDashboard from "./components/ActualsDashboard";

import { AuthenticatedUser, CostCenterInfo, EntraInfo, api, getEntraInfo } from "./api";
import { trackEvent, trackPageView } from "./telemetry";
import "./components/BudgetEditor.css";
import "./App.css";

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inPopupWindow = !!window.opener && window.opener !== window;
  const [popupAuthPending, setPopupAuthPending] = useState(inPopupWindow);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    const saved = localStorage.getItem("authUser");
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (!inPopupWindow) return;

    const completePopupAuth = async () => {
      try {
        await ensureMsalInitialized(msalInstance);
        await msalInstance.handleRedirectPromise();
      } catch {
        // Ignore popup callback errors and return control to opener.
      } finally {
        setPopupAuthPending(false);
        window.close();
      }
    };

    void completePopupAuth();
  }, [inPopupWindow]);

  const isAdmin = authenticatedUser?.role === "admin";
  const hasLeadershipView = authenticatedUser?.role === "cc_leader" || authenticatedUser?.role === "bu_leader" || authenticatedUser?.role === "vp";

  // Active fiscal year from settings API
  const [activeFY, setActiveFY] = useState<string | null>(null);

  // Forecast window status for header note
  const [forecastWindowInfo, setForecastWindowInfo] = useState<{
    windowOpen: boolean;
    activeDim: string | null;
    closeDate: string | null;
    nextWindowDim: string | null;
    nextWindowDate: string | null;
  }>({ windowOpen: false, activeDim: null, closeDate: null, nextWindowDim: null, nextWindowDate: null });

  // Help URL for header button
  const [helpUrl, setHelpUrl] = useState<string>("");
  const [entraInfo, setEntraInfo] = useState<EntraInfo | null>(null);
  const [entraInfoLoaded, setEntraInfoLoaded] = useState(false);
  const [showEntraLinkPrompt, setShowEntraLinkPrompt] = useState(false);
  const [entraPromptMessage, setEntraPromptMessage] = useState("");

  // Multi-CC support: track which CC is currently selected
  // Filter out CCs deactivated for the current FY or earlier
  const allUserCCs = authenticatedUser?.cost_centers || [];
  const fyForFilter = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const userCCs = allUserCCs.filter(cc => {
    if (!cc.deactivated_from_fy) return true;
    return cc.deactivated_from_fy > fyForFilter;
  });
  const [selectedCCNum, setSelectedCCNum] = useState<string | null>(() => {
    const saved = localStorage.getItem("selectedCC");
    return saved || null;
  });
  // Resolve the selected CC (fall back to owned_cost_center or first in list)
  const selectedCC: CostCenterInfo | null = (() => {
    if (userCCs.length === 0) return null;
    return userCCs.find(c => c.cost_center_number === selectedCCNum)
      || userCCs.find(c => c.cost_center_number === authenticatedUser?.owned_cost_center)
      || userCCs[0];
  })();
  const hasOwnCC = !!selectedCC;

  // ── Admin impersonation — view & act as a CC owner ──────────────────────
  // State is intentionally NOT persisted to localStorage (clears on refresh)
  const [simRefreshKey, setSimRefreshKey] = useState(0);
  const bumpSimulation = () => setSimRefreshKey(k => k + 1);

  const [impersonatingCC, setImpersonatingCC] = useState<{
    cost_center_number: string;
    cost_center_name: string;
    cost_center_owner: string;
  } | null>(null);

  const isImpersonating = isAdmin && impersonatingCC !== null;
  const effectiveIsAdmin = isAdmin && !isImpersonating;

  const effectiveCC: CostCenterInfo | null = isImpersonating
    ? { ...impersonatingCC!, deactivated_from_fy: null }
    : selectedCC;
  const effectiveHasOwnCC = isImpersonating ? true : hasOwnCC;

  // Compute FY label and range from activeFY (e.g. "FY25" → April 2025 – March 2026)
  const fyLabel = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const fyNum = parseInt(fyLabel.replace(/\D/g, ''), 10);
  const fyStartYear = 2000 + fyNum;
  const fyEndYear = fyStartYear + 1;
  const fyRange = `April ${fyStartYear} – March ${fyEndYear}`;

  // ── Derive navigation state from URL ──────────────────────────────────────
  const pathSegment = location.pathname.split("/")[1] || "";

  useEffect(() => {
    trackPageView(document.title, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Admin sub-tabs
  const ADMIN_SETUP_URLS = new Set(["business-units", "cost-centers", "users", "accounts"]);
  const adminSubTab = location.pathname.split("/")[2] || "";

  // Primary tab: actuals | forecast | admin-forecast | admin-setup
  const FORECAST_URLS = new Set(["expense", "salary", "headcount", "temp-labor"]);
  const primaryTab = pathSegment === "admin" && ADMIN_SETUP_URLS.has(adminSubTab) ? "admin-setup"
    : pathSegment === "admin" ? "admin-forecast"
    : FORECAST_URLS.has(pathSegment) ? "forecast"
    : "actuals";

  // Sub-tab: the path segment itself
  const subTab = pathSegment || "dashboard";

  const isEntraLinked = !!(entraInfo?.entra_user_id || entraInfo?.is_entra_linked);
  const isSsoSession = (entraInfo?.auth_method || "").toLowerCase() === "entra" || (entraInfo?.auth_method || "").toLowerCase() === "sso";
  const shouldOfferEntraLink = !!authenticatedUser && entraInfoLoaded && !isEntraLinked;

  const ssoButtonLabel = !entraInfoLoaded
    ? "Login with Daikin SSO"
    : isSsoSession && isEntraLinked
      ? "Daikin SSO"
      : isEntraLinked
        ? "Login with Daikin SSO"
        : "Add your Daikin ID to this App";

  // Auth is initialized from localStorage synchronously in useState above

  // Fetch active fiscal year from settings (available to all authenticated users)
  useEffect(() => {
    if (authenticatedUser) {
      api.get("/settings/fiscal-year")
        .then(r => { if (r.data.fiscalYear) setActiveFY(r.data.fiscalYear); })
        .catch(() => {});
      // Fetch role-appropriate help URL
      api.get("/settings/help-url")
        .then(r => {
          setHelpUrl(effectiveIsAdmin ? (r.data.help_url_admin || "") : (r.data.help_url_user || ""));
        })
        .catch(() => {});
    }
  }, [authenticatedUser, effectiveIsAdmin]);

  useEffect(() => {
    if (!authenticatedUser) {
      setEntraInfo(null);
      setEntraInfoLoaded(false);
      setShowEntraLinkPrompt(false);
      setEntraPromptMessage("");
      return;
    }

    let cancelled = false;

    const loadEntraInfo = async () => {
      try {
        const res = await getEntraInfo();
        if (cancelled) return;

        const info = res.data;
        setEntraInfo(info);
        setEntraInfoLoaded(true);

        const isLinked = !!(info.entra_user_id || info.is_entra_linked);
        const isSso = ["entra", "sso"].includes((info.auth_method || "").toLowerCase());
        const promptDismissed = sessionStorage.getItem(`entra-link-prompt-dismissed:${authenticatedUser.id}`) === "1";

        if (!isLinked && !isSso && !promptDismissed && !authenticatedUser.is_first_login) {
          setShowEntraLinkPrompt(true);
        }
      } catch {
        if (!cancelled) {
          setEntraInfoLoaded(true);
        }
      }
    };

    void loadEntraInfo();

    return () => {
      cancelled = true;
    };
  }, [authenticatedUser]);

  // Fetch forecast window config — determines if a window is currently open
  useEffect(() => {
    if (!authenticatedUser || effectiveIsAdmin) return;
    const fetchWindowInfo = async () => {
      try {
        const [configRes, windowsRes] = await Promise.all([
          api.get("/settings/forecast-config"),
          api.get("/settings/dimension-windows", { params: { fy: activeFY || undefined } }),
        ]);
        const adminActive = configRes.data.active as boolean;
        const adminDim = (configRes.data.dimension || null) as string | null;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const windows = (windowsRes.data || []) as { dimension: string; open_from: string; open_to: string }[];

        // Check if today falls within the active dimension's window
        const activeWindow = adminDim
          ? windows.find(w => w.dimension === adminDim && new Date(w.open_from) <= now && new Date(w.open_to) >= now)
          : null;
        const isWindowOpen = adminActive && !!activeWindow;

        // Close date of the currently open window
        const closeDate = activeWindow
          ? new Date(activeWindow.open_to).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;

        // Find the next future window (open_from is after today)
        const futureWindows = windows
          .filter(w => new Date(w.open_from) > now)
          .sort((a, b) => new Date(a.open_from).getTime() - new Date(b.open_from).getTime());
        const next = futureWindows[0] || null;

        setForecastWindowInfo({
          windowOpen: isWindowOpen,
          activeDim: isWindowOpen ? adminDim : null,
          closeDate,
          nextWindowDim: next?.dimension?.replace(" Forecast", "") || null,
          nextWindowDate: next ? new Date(next.open_from).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null,
        });
      } catch { /* silent */ }
    };
    fetchWindowInfo();
  }, [authenticatedUser, effectiveIsAdmin, activeFY]);

  // Listen for real-time FY changes from AdminPanel
  useEffect(() => {
    const handler = (e: Event) => setActiveFY((e as CustomEvent).detail);
    window.addEventListener("fy-changed", handler);
    return () => window.removeEventListener("fy-changed", handler);
  }, []);

  useEffect(() => {
    if (authenticatedUser) {
      if (authenticatedUser.is_first_login && location.pathname !== "/change-password") {
        navigate("/change-password", {
          state: { email: authenticatedUser.email },
        });
      } else if (location.pathname === "/login") {
        if (isAdmin && !isImpersonating) navigate("/admin/bu-dashboard");
        else if (hasOwnCC) navigate("/dashboard");
        else if (hasLeadershipView) navigate("/team");
        else navigate("/dashboard");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser, navigate, location.pathname]);

  const handleLoginSuccess = (user: AuthenticatedUser, token: string) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(user));
    setAuthenticatedUser(user);
    setEntraPromptMessage("");
    trackEvent("frontend_login_success", {
      role: user.role,
      firstLogin: String(user.is_first_login),
    });
  };

  const handleSelectCC = (ccNum: string) => {
    setSelectedCCNum(ccNum);
    localStorage.setItem("selectedCC", ccNum);
  };

  const handleLogout = () => {
    if (authenticatedUser) {
      trackEvent("frontend_logout", { role: authenticatedUser.role });
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("selectedCC");
    setAuthenticatedUser(null);
    setEntraInfo(null);
    setEntraInfoLoaded(false);
    setShowEntraLinkPrompt(false);
    setEntraPromptMessage("");
    navigate("/login");
  };

  const dismissEntraPrompt = () => {
    if (authenticatedUser) {
      sessionStorage.setItem(`entra-link-prompt-dismissed:${authenticatedUser.id}`, "1");
    }
    setShowEntraLinkPrompt(false);
  };

  const handleEntraSsoError = (message: string) => {
    setEntraPromptMessage(message);
    if (showEntraLinkPrompt) {
      dismissEntraPrompt();
    }
  };

  // ── Impersonation handlers ───────────────────────────────────────────────
  // Ref prevents auto-exit from firing during the start transition
  // (location.pathname is still "/admin/..." when impersonation state first applies)
  const impersonationStartingRef = useRef(false);

  const handleStartImpersonation = (ccNum: string, ccName: string, ccOwner: string) => {
    impersonationStartingRef.current = true;
    setImpersonatingCC({ cost_center_number: ccNum, cost_center_name: ccName, cost_center_owner: ccOwner });
    navigate("/dashboard");
  };

  const handleExitImpersonation = () => {
    setImpersonatingCC(null);
    navigate("/admin/bu-dashboard");
  };

  // Auto-exit impersonation if browser-back navigates to an admin route
  useEffect(() => {
    if (impersonationStartingRef.current) {
      impersonationStartingRef.current = false;
      return;
    }
    if (isImpersonating && location.pathname.startsWith("/admin")) {
      setImpersonatingCC(null);
    }
  }, [isImpersonating, location.pathname]);

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!authenticatedUser) return <Navigate to="/login" replace />;
    return <>{children}</>;
  };

  // Header role label
  const roleLabel = isImpersonating ? "Admin · Viewing as CC Owner"
    : isAdmin ? "Admin"
    : authenticatedUser?.role === "vp" ? "VP"
    : authenticatedUser?.role === "bu_leader" ? "BU Leader"
    : authenticatedUser?.role === "cc_leader" ? "CC Leader"
    : "";

  // Whether CC pills should show (My Cost Centers or Forecast views, not My Team)
  const showCCPills = !effectiveIsAdmin && !isImpersonating && userCCs.length > 1 && subTab !== "team";

  // Whether scope toggle row should show (Actuals mode, both CC and team available)
  const showScopeRow = !effectiveIsAdmin && !isImpersonating && primaryTab === "actuals" && hasOwnCC && hasLeadershipView;

  // Page title for header row 2
  const PAGE_TITLES: Record<string, string> = {
    dashboard: "Monthly Spend Review",
    team: "Team Overview",
  };
  const pageTitle = PAGE_TITLES[subTab] || "";
  const showHeaderRow2 = !effectiveIsAdmin && authenticatedUser && (pageTitle || showScopeRow);

  const AppLayout = () => (
    <div>
      <div className={`app-header${showHeaderRow2 ? " app-header--has-row2" : ""}`}>
        {/* Row 1 left: title + meta */}
        <div className="app-header-left">
          <span className="app-header-title">
            QFT {roleLabel && `· ${roleLabel}`}
            <span className="fy-badge">{fyLabel}</span>
            <span className="fy-range">({fyRange})</span>
          </span>
          {authenticatedUser && (
            <span className="app-header-meta">
              <span className="app-header-meta-label">User</span>
              {authenticatedUser.name}
              {effectiveHasOwnCC && (isImpersonating || userCCs.length <= 1) && (
                <>
                  <span className="app-header-meta-sep">·</span>
                  <span className="app-header-meta-label">CC</span>
                  {effectiveCC!.cost_center_number} – {effectiveCC!.cost_center_name}
                </>
              )}
            </span>
          )}
        </div>

        {/* Budgeting Window status (forecast pages) */}
        {!effectiveIsAdmin && primaryTab === "forecast" && (
          <div className={`forecast-window-note ${forecastWindowInfo.windowOpen ? "forecast-window-note--open" : "forecast-window-note--closed"}`}>
            <div className="fw-row">
              <span className="fw-label">Budgeting Window</span>
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-status fw-status--open">Open</span>
              ) : (
                <span className="fw-status fw-status--closed">Closed</span>
              )}
            </div>
            <div className="fw-row-detail">
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-detail">
                  {forecastWindowInfo.activeDim?.replace(" Forecast", "")} Forecast closes <strong>{forecastWindowInfo.closeDate}</strong>
                </span>
              ) : forecastWindowInfo.nextWindowDate && forecastWindowInfo.nextWindowDim ? (
                <span className="fw-detail">
                  {forecastWindowInfo.nextWindowDim} Forecast opens <strong>{forecastWindowInfo.nextWindowDate}</strong>
                </span>
              ) : (
                <span className="fw-detail">No upcoming windows scheduled</span>
              )}
            </div>
          </div>
        )}

        {/* Row 1 center: primary nav tabs */}
        {!effectiveIsAdmin && (
          <div className="nav-tabs app-header-nav">
            <button
              onClick={() => navigate(effectiveHasOwnCC ? "/dashboard" : "/team")}
              className={`nav-tab${primaryTab === "actuals" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">bar_chart</span>
              Actuals
            </button>
            {effectiveHasOwnCC && (
              <button
                onClick={() => navigate("/expense")}
                className={`nav-tab${primaryTab === "forecast" ? " nav-tab--active" : ""}`}
              >
                <span className="material-icons">trending_up</span>
                Forecast
              </button>
            )}
          </div>
        )}

        {effectiveIsAdmin && (
          <div className="nav-tabs app-header-nav">
            <button
              onClick={() => navigate("/admin/bu-dashboard")}
              className={`nav-tab${primaryTab === "admin-forecast" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">tune</span>
              Forecast Setup
            </button>
            <button
              onClick={() => navigate("/admin/business-units")}
              className={`nav-tab${primaryTab === "admin-setup" ? " nav-tab--active" : ""}`}
            >
              <span className="material-icons">admin_panel_settings</span>
              Admin Setup
            </button>
          </div>
        )}

        {/* Row 1 right: help + logout */}
        <div className="app-header-actions">
          {authenticatedUser && (
            <EntraLoginButton
              className="header-sso-btn"
              label={ssoButtonLabel}
              showSuccess={isSsoSession && isEntraLinked}
              disabled={isSsoSession && isEntraLinked}
              onError={handleEntraSsoError}
              onSuccess={(user, token) => {
                setShowEntraLinkPrompt(false);
                setEntraPromptMessage("");
                handleLoginSuccess(user, token);
              }}
            />
          )}

          <button
            onClick={() => {
              if (helpUrl) window.open(helpUrl, "_blank", "noopener");
              else alert(effectiveIsAdmin ? "Configure help URLs in Forecast Setup > Forecast Windows." : "Help page not configured yet. Contact your admin.");
            }}
            className="help-btn"
            title="Help"
          >
            <span className="material-icons">help_outline</span>
            Help
          </button>
          <button onClick={handleLogout} className="logout-btn">
            <span className="material-icons">logout</span>
            Logout
          </button>
        </div>

        {/* Row 2 left: page title + subtitle, right: page controls */}
        {showHeaderRow2 && pageTitle && (
          <div className="app-header-page-title-group">
            <div className="app-header-page-title-left">
              <span className="app-header-page-title">{pageTitle}</span>
              <div id="page-subtitle-slot" />
            </div>
            <div id="page-title-controls-slot" />
          </div>
        )}

        {/* Row 2 center: scope toggle (aligned with nav-tabs above) */}
        {showHeaderRow2 && showScopeRow && (
          <div className="scope-tabs app-header-scope">
            <button
              onClick={() => navigate("/dashboard")}
              className={`scope-tab${subTab === "dashboard" ? " scope-tab--active" : ""}`}
            >
              <span className="material-icons">account_balance</span>
              My Cost Centers
            </button>
            <button
              onClick={() => navigate("/team")}
              className={`scope-tab${subTab === "team" ? " scope-tab--active" : ""}`}
            >
              <span className="material-icons">groups</span>
              My Team
            </button>
          </div>
        )}
      </div>

      {showEntraLinkPrompt && shouldOfferEntraLink && (
        <div className="entra-link-prompt" role="status" aria-live="polite">
          <div className="entra-link-prompt__text">
            Add your Daikin SSO identity to this app now for faster sign-in and account linking.
          </div>
          <div className="entra-link-prompt__actions">
            <EntraLoginButton
              className="entra-link-prompt__btn"
              label="Add your Daikin ID to this App"
              onError={handleEntraSsoError}
              onSuccess={(user, token) => {
                setShowEntraLinkPrompt(false);
                setEntraPromptMessage("");
                handleLoginSuccess(user, token);
              }}
            />
            <button type="button" className="entra-link-prompt__dismiss" onClick={dismissEntraPrompt}>
              Not now
            </button>
          </div>
        </div>
      )}

      {!!entraPromptMessage && !showEntraLinkPrompt && (
        <div className="entra-link-message" role="alert">
          {entraPromptMessage}
        </div>
      )}

      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="impersonation-banner">
          <span className="material-icons">visibility</span>
          <span className="impersonation-banner-text">
            Viewing as <strong>{impersonatingCC!.cost_center_number} – {impersonatingCC!.cost_center_name}</strong>
            {impersonatingCC!.cost_center_owner && (
              <span className="impersonation-banner-owner"> (Owner: {impersonatingCC!.cost_center_owner})</span>
            )}
          </span>
          <button className="impersonation-banner-exit" onClick={handleExitImpersonation}>
            <span className="material-icons">close</span>
            Exit View
          </button>
        </div>
      )}

      {/* CC selector row — only rendered when multiple cost centers are available */}
      {showCCPills && (
        <div className="cc-content-row">
          <div className="cc-selector">
            {userCCs.map(cc => (
              <button
                key={cc.cost_center_number}
                className={`cc-pill${cc.cost_center_number === selectedCC?.cost_center_number ? " cc-pill--active" : ""}`}
                onClick={() => handleSelectCC(cc.cost_center_number)}
                title={cc.cost_center_name}
              >
                {cc.cost_center_number} – {cc.cost_center_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Forecast sub-tabs + cycle switcher + window status */}
      {!effectiveIsAdmin && primaryTab === "forecast" && (
        <div className="app-sub-nav">
          <div className="app-sub-nav-left">
            <button
              onClick={() => navigate("/expense")}
              className={`nav-tab nav-tab--sub nav-tab--sub-expense${subTab === "expense" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">receipt_long</span>
              Expense
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/salary")}
              className={`nav-tab nav-tab--sub nav-tab--sub-salary${subTab === "salary" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">payments</span>
              Salary &amp; BIP
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/temp-labor")}
              className={`nav-tab nav-tab--sub nav-tab--sub-temp-labor${subTab === "temp-labor" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">engineering</span>
              Contract/Outside Services
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/headcount")}
              className={`nav-tab nav-tab--sub nav-tab--sub-headcount${subTab === "headcount" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">group</span>
              Headcount
            </button>
          </div>
          <div className="app-sub-nav-right">
            <div id="forecast-cycle-slot" />
          </div>
        </div>
      )}

      <div style={{ padding: "0 24px" }}>
        {!effectiveIsAdmin && (subTab === "salary" || subTab === "temp-labor") && effectiveCC && (
          <YTGSimulationStrip key={simRefreshKey} costCenterNumber={effectiveCC.cost_center_number} />
        )}
        <Outlet />
      </div>
    </div>
  );

  if (popupAuthPending) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        Completing sign in...
      </div>
    );
  }

  return (
    <div className="app-container">
      <Routes>
        <Route path="/login" element={<LoginView onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/callback" element={<EntraCallback onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/change-password" element={<ChangePasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Admin views */}
          <Route path="/admin" element={<Navigate to="/admin/bu-dashboard" replace />} />
          <Route path="/admin/:tab" element={<AdminPanel onImpersonate={handleStartImpersonation} />} />

          {/* Actuals > My Team (Actuals Dashboard scoped to user's BUs/CCs) */}
          <Route
            path="/team"
            element={
              authenticatedUser && (hasLeadershipView || effectiveIsAdmin) && !isImpersonating
                ? <ActualsDashboard apiPrefix="/team" fiscalYear={activeFY || fyLabel} />
                : <Navigate to="/dashboard" replace />
            }
          />

          {/* Actuals > My Cost Centers */}
          <Route
            path="/dashboard"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <Dashboard costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} />
                : null
            }
          />

          {/* Forecast > Expense */}
          <Route
            path="/expense"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <BudgetEditor costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} costCenterOwner={effectiveCC!.cost_center_owner} fiscalYear={fyLabel} isAdmin={effectiveIsAdmin} />
                : null
            }
          />
          <Route
            path="/headcount"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <HeadcountEditor costCenterNumber={effectiveCC!.cost_center_number} isAdmin={effectiveIsAdmin} />
                : null
            }
          />
          <Route
            path="/salary"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <SalaryView costCenterNumber={effectiveCC!.cost_center_number} isAdmin={effectiveIsAdmin} onDataSaved={bumpSimulation} />
                : null
            }
          />
          <Route path="/bip" element={<Navigate to="/salary" replace />} />
          <Route
            path="/temp-labor"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <TempLaborView costCenterNumber={effectiveCC!.cost_center_number} onDataSaved={bumpSimulation} />
                : null
            }
          />

          {/* Root redirect */}
          <Route path="/" element={
            <Navigate to={
              effectiveIsAdmin ? "/admin/forecast-windows"
              : effectiveHasOwnCC ? "/dashboard"
              : hasLeadershipView ? "/team"
              : "/dashboard"
            } replace />
          } />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => (
  <MsalProvider instance={msalInstance}>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </MsalProvider>
);

export default App;
